import React, { useEffect, useState } from "react";
import AdminShell from "@/components/AdminShell";
import { getToken } from "@/lib/auth";

const STATUS_COLORS = {
  pending:    "bg-yellow-500/15 text-yellow-400",
  approved:   "bg-emerald-500/15 text-emerald-400",
  rejected:   "bg-red-500/15 text-red-400",
  waitlisted: "bg-blue-500/15 text-blue-400",
};

export default function AdminPrivateApplications() {
  const [apps,    setApps]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [expanded, setExpanded] = useState(null);
  const [notes, setNotes] = useState({});
  const [busy,    setBusy]    = useState("");
  const [actionMsg, setActionMsg] = useState({});
  const [msgOpen,  setMsgOpen]  = useState(null);
  const [msgBody,  setMsgBody]  = useState("");
  const [msgBusy,  setMsgBusy]  = useState(false);
  const [msgResult, setMsgResult] = useState("");

  useEffect(() => {
    const token = getToken();
    fetch("/api/admin/private-applications", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((d) => { setApps(d); setLoading(false); })
      .catch((e) => { setError("Failed to load: " + e); setLoading(false); });
  }, []);

  const updateStatus = async (appId, status, userId) => {
    setBusy(appId);
    setActionMsg((m) => ({ ...m, [appId]: "" }));
    try {
      const token = getToken();
      const res = await fetch(`/api/admin/private-applications/${appId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status, admin_note: notes[appId] || "" }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Failed");
      setApps((prev) => prev.map((a) => a.application_id === appId ? { ...a, status } : a));
      setActionMsg((m) => ({ ...m, [appId]: `Status set to ${status}.` }));
      setNotes((prev) => ({ ...prev, [appId]: "" }));
    } catch (e) {
      setActionMsg((m) => ({ ...m, [appId]: "Error: " + e.message }));
    } finally {
      setBusy("");
    }
  };

  const sendMessage = async (userId) => {
    if (!msgBody.trim() || !userId) return;
    setMsgBusy(true); setMsgResult("");
    try {
      const token = getToken();
      const res = await fetch(`/api/admin/concierge/${userId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ body: msgBody.trim() }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Failed");
      setMsgResult("Message sent."); setMsgBody(""); setMsgOpen(null);
    } catch (e) { setMsgResult("Error: " + e.message); }
    finally { setMsgBusy(false); }
  };

  return (
    <AdminShell>
      <div className="p-6 md:p-10 text-white">
        <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-2">Admin</div>
        <h1 className="font-display text-3xl mb-6">Private Applications</h1>

        {loading && <div className="text-white/40">Loading…</div>}
        {error   && <div className="text-red-400">{error}</div>}

        {!loading && !error && apps.length === 0 && (
          <div className="text-white/30 text-sm">No applications yet.</div>
        )}

        {!loading && !error && apps.length > 0 && (
          <div className="space-y-3">
            {apps.map((a) => (
              <div key={a.application_id} className="glass rounded-2xl p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="font-medium text-white">{a.full_name || "—"}</div>
                    <div className="text-white/50 text-sm">{a.email} · {a.city}</div>
                    <div className="text-white/30 text-xs mt-1">{a.created_at ? a.created_at.slice(0, 10) : "—"}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                      {a.user_id && (
                        <button
                          onClick={() => { setMsgOpen(a.application_id); setMsgResult(""); setMsgBody(""); }}
                          className="rounded-xl px-3 py-1 text-xs font-medium bg-[rgba(212,175,55,0.12)] hover:bg-[rgba(212,175,55,0.22)] text-[#F2DE94] border border-[rgba(212,175,55,0.2)] transition-colors"
                        >
                          Message
                        </button>
                      )}
                    <span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[a.status] || "bg-white/5 text-white/40"}`}>
                      {a.status}
                    </span>
                    <button
                      onClick={() => setExpanded(expanded === a.application_id ? null : a.application_id)}
                      className="text-xs text-white/40 hover:text-white/70 transition-colors underline underline-offset-2"
                    >
                      {expanded === a.application_id ? "Collapse" : "Review"}
                    </button>
                  </div>
                </div>

                {expanded === a.application_id && (
                  <div className="mt-4 space-y-3">
                    <div className="text-xs text-white/40 uppercase tracking-wider">Why they applied</div>
                    <p className="text-sm text-white/80 leading-relaxed">{a.why || "—"}</p>
                    {a.social && (
                      <div className="text-xs">
                        <span className="text-white/40">Social: </span>
                        <a href={a.social} target="_blank" rel="noreferrer" className="text-[#D4AF37] underline underline-offset-2">{a.social}</a>
                      </div>
                    )}
                    {a.admin_note && (
                      <div className="text-xs text-white/50 italic">Admin note: {a.admin_note}</div>
                    )}

                    <textarea
                      value={notes[a.application_id] || ""}
                      onChange={(e) => setNotes((prev) => ({ ...prev, [a.application_id]: e.target.value }))}
                      rows={2}
                      placeholder="Optional admin note…"
                      className="w-full rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-sm text-white placeholder:text-white/25 outline-none focus:border-[rgba(212,175,55,0.4)] resize-none"
                    />

                    {actionMsg[a.application_id] && (
                      <p className={`text-xs ${actionMsg[a.application_id].startsWith("Error") ? "text-red-400" : "text-emerald-400"}`}>
                        {actionMsg[a.application_id]}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-2">
                      {["approved", "rejected", "waitlisted", "pending"].map((s) => (
                        <button
                          key={s}
                          disabled={busy === a.application_id || a.status === s}
                          onClick={() => updateStatus(a.application_id, s, a.user_id)}
                          className={`rounded-xl px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-40 capitalize border ${
                            s === "approved"   ? "bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border-emerald-500/20" :
                            s === "rejected"   ? "bg-red-500/10 hover:bg-red-500/20 text-red-400 border-red-500/20" :
                            s === "waitlisted" ? "bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border-blue-500/20" :
                                                 "bg-white/5 hover:bg-white/10 text-white/60 border-white/10"
                          }`}
                        >
                          {busy === a.application_id ? "…" : s}
                        </button>
                      ))}

                      {a.user_id && (
                        <button
                          onClick={() => { setMsgOpen(a.application_id); setMsgResult(""); setMsgBody(""); }}
                          className="rounded-xl px-3 py-1.5 text-xs font-medium bg-[rgba(212,175,55,0.1)] hover:bg-[rgba(212,175,55,0.2)] text-[#F2DE94] border border-[rgba(212,175,55,0.2)] transition-colors"
                        >
                          Message User
                        </button>
                      )}
                    </div>

                    {msgOpen === a.application_id && (
                      <div className="space-y-2 pt-1">
                        <textarea
                          value={msgBody}
                          onChange={(e) => setMsgBody(e.target.value)}
                          rows={2}
                          placeholder="Concierge message to user…"
                          className="w-full rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-sm text-white placeholder:text-white/25 outline-none focus:border-[rgba(212,175,55,0.4)] resize-none"
                        />
                        {msgResult && <p className={`text-xs ${msgResult.startsWith("Error") ? "text-red-400" : "text-emerald-400"}`}>{msgResult}</p>}
                        <div className="flex gap-2">
                          <button onClick={() => sendMessage(a.user_id)} disabled={msgBusy || !msgBody.trim()}
                            className="rounded-xl px-4 py-1.5 text-xs font-medium bg-[rgba(212,175,55,0.15)] hover:bg-[rgba(212,175,55,0.25)] text-[#F2DE94] disabled:opacity-40 transition-colors">
                            {msgBusy ? "Sending…" : "Send"}
                          </button>
                          <button onClick={() => setMsgOpen(null)}
                            className="text-xs text-white/35 hover:text-white/60 transition-colors px-2">
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminShell>
  );
}
